import { notify } from '../notifications';

/**
 * Приглашение: копирование ссылки и QR.
 *
 * Обе кнопки делают одно и то же действие — кладут в буфер — и ведут
 * себя одинаково: текст кнопки на две секунды меняется на «Скопировано».
 * Так человек видит, что нажатие сработало, и дальше просто вставляет
 * картинку в мессенджер (уточнение владельца 16.10).
 *
 * Прежде на устройстве с Web Share открывалось системное окно
 * «Поделиться». Это оказалось хуже: окно есть и на десктопном Chrome,
 * оно перехватывало нажатие, и вместо мгновенного копирования человек
 * получал лишний экран выбора.
 *
 * Поэтому порядок такой: буфер обмена, если браузер его не даёт —
 * системное «Поделиться», если нет и его — скачивание файла. Кнопка
 * обязана сделать что-то полезное, а не промолчать.
 *
 * В буфер всегда уходит чёрный QR на белом, независимо от темы:
 * инвертированный код читают не все сканеры, а картинка уходит
 * в чужую переписку, где тему никто не выбирает.
 */

// Запасной путь, когда буфер недоступен: системное окно «Поделиться».
function canShareFiles() {
    return !!(navigator.share && navigator.canShare);
}

function qrUrl(block, format) {
    const kind = block.getAttribute('data-invite-kind') || 'client';
    return `/qr/${kind}.${format}`;
}

async function fetchQrFile(block) {
    const response = await fetch(qrUrl(block, 'png'), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`QR request failed: ${response.status}`);
    const blob = await response.blob();
    const kind = block.getAttribute('data-invite-kind') || 'client';
    return { blob, file: new File([blob], `body-id-${kind}.png`, { type: 'image/png' }) };
}

function download(blob, name) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Отзываем не сразу: Safari успевает начать скачивание не мгновенно.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Подпись на кнопке на пару секунд: без ответа непонятно, сработало ли.
// Подписи разные для ссылки и картинки: одинаковое «Скопировано»
// на соседних кнопках не говорит, что именно попало в буфер (QA 17.10).
function flash(button, text) {
    if (!button || button.dataset.flashing === '1') return;
    const original = button.textContent;
    button.dataset.flashing = '1';
    button.textContent = text;
    setTimeout(() => {
        button.textContent = original;
        button.dataset.flashing = '0';
    }, 2000);
}

async function copyLink(block, button) {
    const url = block.getAttribute('data-invite-url') || '';
    if (!url) return;
    try {
        await navigator.clipboard.writeText(url);
        flash(button, 'Ссылка скопирована');
    } catch (error) {
        console.error(error);
        notify('Браузер не дал скопировать ссылку — выделите её под кодом', 'warning');
    }
}

async function copyImage(block, button) {
    try {
        const { blob, file } = await fetchQrFile(block);

        if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
            await navigator.clipboard.write([
                new window.ClipboardItem({ 'image/png': blob }),
            ]);
            flash(button, 'QR-код скопирован');
            return;
        }

        if (canShareFiles() && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] });
            flash(button, 'QR-код отправлен');
            return;
        }

        download(blob, 'body-id-qr.png');
        // Ответ нужен и здесь: браузер без доступа к буферу обмена
        // уводит в скачивание, и прежде кнопка после этого молчала —
        // человек не понимал, сработало или нет (приёмка 18.10).
        flash(button, 'QR-код скачан');
    } catch (error) {
        // Отмена в окне «Поделиться» приходит той же ошибкой, что и отказ:
        // ругаться на неё не нужно.
        if (error && error.name === 'AbortError') return;
        console.error(error);
        try {
            const { blob } = await fetchQrFile(block);
            download(blob, 'body-id-qr.png');
            flash(button, 'QR-код скачан');
        } catch (downloadError) {
            console.error(downloadError);
            notify('Не удалось получить QR-код, обновите страницу', 'error');
        }
    }
}

export function initInvite() {
    // Само окно открывает static/js/main.js по классу `open-modal`:
    // в бандле своя копия jQuery, и плагина магнифика на ней нет —
    // вызов отсюда молча ничего не делал (findings/frontend-two-jquery).
    //
    // Обработчик на документе: блок живёт и в окне, и на странице,
    // а окно магнифик перемещает в DOM при открытии.
    document.addEventListener('click', (event) => {
        const target = event.target.closest
            ? event.target.closest('.action__invite-copy-link, .action__invite-copy-image')
            : null;
        if (!target) return;
        const block = target.closest('.invite');
        if (!block) return;
        if (target.classList.contains('action__invite-copy-link')) {
            copyLink(block, target);
        } else {
            copyImage(block, target);
        }
    });
}
